<template>
  <div class="points-accounts">
    <!-- Header -->
    <div class="header">
      <button class="btn-back" @click="onClickLeft" aria-label="返回">←</button>
      <span class="header-title">积分账户</span>
      <span class="header-help" @click="showHelp = true">查看帮助</span>
    </div>

    <!-- ═══════════ 页签切换 ═══════════ -->
    <van-tabs
      v-model="activeTab"
      color="#2563eb"
      title-active-color="#2563eb"
      title-inactive-color="#8b95a5"
      line-width="28"
      line-height="3"
      class="custom-tabs"
    >
      <!-- ──── 积分页签（保持原有内容）──── -->
      <van-tab title="积分">
        <!-- 总积分卡片 -->
        <div class="summary-card">
          <div class="summary-icon">💎</div>
          <div class="summary-info">
            <div class="summary-label">总积分</div>
            <div class="summary-value">
              {{ totalPoints.toLocaleString() }}<span class="unit">分</span>
            </div>
            <div class="summary-sub">共 {{ accounts.length }} 个账户</div>
          </div>
        </div>

        <!-- 账户列表标题 -->
        <div class="section-header">
          <span class="title">账户列表</span>
          <span class="count">共 <strong>{{ accounts.length }}</strong> 个账户</span>
        </div>

        <!-- 空状态 -->
        <van-empty v-if="!accounts.length" description="暂无积分账户" />

        <!-- 账户列表 -->
        <div v-else class="account-list">
          <div
            v-for="account in accounts"
            :key="account.phone"
            class="account-card"
          >
            <div class="ac-header">
              <span class="ac-phone">{{ account.phone }}</span>
            </div>
            <div class="ac-body">
              <div class="ac-balance-area">
                <div class="ac-balance">
                  {{ account.balance.toLocaleString() }}<span class="unit">分</span>
                </div>
              </div>
              <van-button
                type="info"
                size="small"
                round
                @click="usePoints(account)"
              >查看订单</van-button>
            </div>
          </div>
        </div>
      </van-tab>

      <!-- ──── 订单页签 ──── -->
      <van-tab title="订单" :disabled="orderTabDisabled">
        <van-list
          v-model="orderLoading"
          :finished="orderFinished"
          :finished-text="orderList.length ? '没有更多订单了' : ''"
          loading-text="加载中..."
          @load="onOrderLoad"
        >
          <div v-if="orderList.length === 0 && !orderLoading" class="order-empty">
            <van-empty description="暂无订单记录" />
          </div>
          <div v-else class="order-list">
            <div
              v-for="order in orderList"
              :key="order.orderNo"
              class="pageSmallCard listItem"
            >
              <div>
                <div class="pageFlexBetween title">
                  <p>稳易云积分</p>
                  <span class="pageFontWeight">{{ formatMoney(order.quotaPayAmt, 2) }}元</span>
                </div>
                <div class="line"></div>
                <div class="detail">
                  <div class="pageFlexColumn icons">
                    <div class="pageBgBlock">
                      <van-icon name="alipay" size="40" />
                    </div>
                  </div>
                  <div class="list">
                    <li>订单编号：{{ order.orderNo }}</li>
                    <li v-if="order.employeeNo">会员帐号：{{ order.employeeNo }}</li>
                    <li>完成时间：{{ order.finishTime }}</li>
                    <li>交易类型：<span :style="order.transType === 'PAY' ? 'color: #07c160' : 'color: red'">{{ order.transType === 'PAY' ? '支付' : '退款' }}</span></li>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </van-list>
      </van-tab>
    </van-tabs>

    <!-- 查看帮助弹窗 -->
    <van-popup
      v-model="showHelp"
      round
      :style="{ width: '85%', padding: '24px 20px', maxHeight: '70vh', overflowY: 'auto' }"
    >
      <div class="help-popup">
        <h3 class="help-title">稳易云积分 · 帮助说明</h3>
        <div class="help-content">
          <ul>
            <li>
              <b>稳易云积分产品说明：</b>
              <p>- “稳易云积分”可用于支付宝支付场景消费抵扣使用；</p>
              <p>- 使用支付宝扫码支付或出示付款码均可抵扣；</p>
              <p>- 付款方式请选择因公付（麦的好 Y）；</p>
              <p>- 单日消耗最大支持 999 元；</p>
              <p>- 如有疑问请联系客服；</p>
            </li>
          </ul>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { NavBar, Tag, Button, Empty, Tab, Tabs, List, Icon, Popup } from 'vant'
import { getPoiontsAccountByCurrentUserApi, getWheatGrainBillListApi } from '@/api/user'

export default {
  name: 'PointsAccounts',

  components: {
    [NavBar.name]: NavBar,
    [Tag.name]: Tag,
    [Button.name]: Button,
    [Empty.name]: Empty,
    [Tab.name]: Tab,
    [Tabs.name]: Tabs,
    [List.name]: List,
    [Icon.name]: Icon,
    [Popup.name]: Popup
  },

  data() {
    return {
      activeTab: 0,
      accounts: [],

      // 订单相关
      orderList: [],
      orderLoading: false,
      orderFinished: false,
      orderPage: 0,
      orderPageSize: 5,
      orderTotal: 0,
      phone: '',
      orderTabDisabled: true,
      showHelp: false
    }
  },

  computed: {
    totalPoints() {
      return this.accounts.reduce((sum, a) => sum + a.balance, 0)
    },

    maxBalance() {
      if (!this.accounts.length) return 0
      return Math.max(...this.accounts.map(a => a.balance))
    }
  },

  created() {
    this.fetchAccounts()
  },

  watch: {
    // 切换到订单页签时，列表为空则主动触发首次加载
    activeTab(newValue) {
      if (newValue === 1 && this.orderList?.length === 0 && !this.orderLoading) {
        this.onOrderLoad()
      }
      if (newValue === 0) {
        this.orderList = []
        this.orderLoading = false
        this.orderFinished = false
        this.orderPage = 0
        this.orderTotal = 0
        this.phone = ''
        this.orderTabDisabled = true
      }
    }
  },

  methods: {
    async fetchAccounts() {
      const res = await getPoiontsAccountByCurrentUserApi()
      this.accounts = res.data
    },

    // 麦粒消费记录查询
    usePoints(account) {
      this.phone = account.phone
      this.orderTabDisabled = false
      this.$nextTick(() => {
        this.activeTab = 1
      })
    },

    onClickLeft() {
      this.$router.back()
    },

    // ── 订单分页加载 ──────────────────────────────────────────
    onOrderLoad() {
      this.orderPage++
      // const mock = this._generateOrderPage(this.orderPage, this.orderPageSize)
      const params = {
        phone: this.phone,
        page: this.orderPage,
        pageSize: this.orderPageSize
      }
      getWheatGrainBillListApi(params).then(({status, data, message}) => {
        if(status === 200) {
          this.orderTotal = data.totalCount
          this.orderList = this.orderList.concat(data.paymentList)
          if (data.paymentList.length < this.orderPageSize) {
            this.orderFinished = true
          }
        } else {
          this.$alert(message)
        }
        this.orderLoading = false
      })
    },

    // 生成模拟订单数据（共 23 条，展示 5 页效果）
    _generateOrderPage(page, size) {
      const allOrders = this._mockAllOrders()
      const start = (page - 1) * size
      return allOrders.slice(start, start + size)
    },

    _mockAllOrders() {
      const accounts = [
        '138****6789', '139****1234', '177****8899',
        '136****5566', '158****3490', '186****2017'
      ]
      const amounts = [50.00, 100.00, 200.00, 300.00, 500.00, 88.50, 168.00, 299.00]

      const list = []
      for (let i = 0; i < 23; i++) {
        const d = new Date(2026, 5, 5)
        d.setHours(d.getHours() - i * 3)
        const time = this._fmt(d)
        list.push({
          orderNo: 'WYY' + String(202606050000 + i).slice(2),
          employeeNo: accounts[i % accounts.length],
          quotaPayAmt: amounts[i % amounts.length],
          finishTime: time
        })
      }
      return list
    },

    formatMoney(amount, fixed = 2) {
      if (amount == null || isNaN(amount)) return '0.0'
      const amountInYuan = amount / 100;
      return amountInYuan.toFixed(fixed);
    },

    _fmt(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const mi = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${d} ${h}:${mi}:${s}`
    }
  }
}
</script>

<style scoped lang="less">
/* ── Header ──────────────────────── */
.header {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 0 16px;
}
.btn-back {
  width: 36px; height: 36px; border-radius: 50%;
  border: 1px solid #e8ecf1; background: #fff;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 16px; color: #5a6878;
  transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1); flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.btn-back:hover { background: #f8f9fb; border-color: #cdd4de; }
.btn-back:active { transform: scale(0.95); }
.header-title {
  font-size: 18px; font-weight: 700; color: #1a2332;
  letter-spacing: -0.3px;
  flex: 1;
}
.header-help {
  font-size: 13px;
  color: #2563eb;
  cursor: pointer;
  white-space: nowrap;
  padding: 4px 8px;
}
.header-help:active { opacity: 0.7; }

/* ========== 页签圆角 + 吸顶宽度约束 ========== */
.custom-tabs {
  /deep/ .van-tab {
    border-radius: 5px;
  }
  /deep/ .van-tabs__nav {
    border-radius: 5px;
  }
  /deep/ .van-tab--disabled {
    color: #c8cfd8;
    cursor: not-allowed;
  }
  /deep/ .van-sticky--fixed {
    left: 16px;
    right: 16px;
    width: auto;
  }
}

/* ========== CSS Variables ========== */
.points-accounts {
  --bg-page: #f0f4f8;
  --bg-card: #ffffff;
  --bg-section: #f8f9fb;
  --border: #e2e6ed;
  --border-active: #bcc4d2;
  --text-primary: #1a2332;
  --text-secondary: #5a6878;
  --text-muted: #8b95a5;
  --radius-sm: 6px;
  --radius: 10px;
  --radius-lg: 14px;
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04);
  --transition: 180ms cubic-bezier(0.4, 0, 0.2, 1);

  background: var(--bg-page);
  min-height: 100vh;
  min-height: 100dvh;
  color: var(--text-primary);
  line-height: 1.5;
  padding: 12px 16px 48px;
}

/* ========== 总积分卡片 ========== */
.summary-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 20px;
  margin: 12px 0 16px;
  box-shadow: var(--shadow-sm);
  display: flex;
  align-items: center;
  gap: 16px;
}

.summary-icon {
  width: 48px;
  height: 48px;
  border-radius: var(--radius);
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}

.summary-info { flex: 1; min-width: 0; }

.summary-label {
  font-size: 12px;
  color: var(--text-muted);
  font-weight: 500;
  margin-bottom: 2px;
}

.summary-value {
  font-family: "SF Mono", "JetBrains Mono", "Consolas", monospace;
  font-size: 28px;
  font-weight: 700;
  color: #2563eb;
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.summary-value .unit {
  font-size: 14px;
  font-weight: 500;
  font-family: "PingFang SC", "Microsoft YaHei", "Hiragino Sans GB", -apple-system, sans-serif;
  margin-left: 2px;
}

.summary-sub {
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 2px;
}

/* ========== 分段标题 ========== */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.section-header .title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
}

.section-header .count {
  font-size: 12px;
  color: var(--text-muted);
}

.section-header .count strong { color: var(--text-primary); }

/* ========== 账户列表 ========== */
.account-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 16px;
}

.account-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  transition: box-shadow var(--transition);
}

/* ========== 卡头 ========== */
.ac-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: var(--bg-section);
  border-bottom: 1px solid var(--border);
}

.ac-phone {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

/* ========== 卡身 ========== */
.ac-body {
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.ac-balance-area { flex: 1; min-width: 0; }

.ac-balance {
  font-family: "SF Mono", "JetBrains Mono", "Consolas", monospace;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.3px;
  line-height: 1.2;
}

.ac-balance .unit {
  font-size: 13px;
  font-weight: 500;
  font-family: "PingFang SC", "Microsoft YaHei", "Hiragino Sans GB", -apple-system, sans-serif;
  color: var(--text-secondary);
  margin-left: 1px;
}

/* ================================================================
   订单列表样式（对齐 OrderItem.vue）
   ================================================================ */
.order-empty {
  padding: 40px 0;
}

.listItem {
  width: 100%;
  padding: 0;
  margin-top: 15px;
  min-height: 90px;
  font-size: var(--pFont);

  .title {
    padding: 10px 0;
    font-size: var(--titleFont);
    color: var(--titleColor);
    p {
      margin-left: 24%;
    }
    span {
      color: var(--redColor);
      margin-right: 13px;
      max-width: 95px;
      min-width: 50px;
      text-align: right;
    }
  }

  .line {
    height: 5px;
    margin: 0 2px;
    margin-top: -5px;
    box-shadow: 0px 4px 4px 2px #f4f4f4;
  }

  .detail {
    padding: 12px 0;
    display: flex;

    .icons {
      width: 19%;
      color: #666;
      margin: 0 10px;
      display: inline-flex;
      vertical-align: top;

      .pageBgBlock {
        color: #fff;
        width: 50px;
        height: 50px;
        background: var(--redColor);
        margin-bottom: 4px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    .list {
      width: 66%;
      font-size: var(--smallFont);
      color: var(--hidColor) !important;
      display: inline-block;
      line-height: 1.8;

      li {
        word-break: break-all;
      }
    }
  }
}

/* ========== 帮助弹窗 ========== */
.help-popup {
  .help-title {
    font-size: 17px;
    font-weight: 700;
    color: var(--text-primary);
    text-align: center;
    margin-bottom: 18px;
  }

  .help-content {
    ul {
      list-style: disc;
      margin-left: 15px;
    }

    li {
      list-style: disc;
      margin-bottom: 14px;

      b {
        color: rgba(0, 0, 0, 0.7);
      }

      p {
        color: rgba(0, 0, 0, 0.53);
        margin-top: 4px;
        line-height: 1.6;
      }
    }
  }
}

/* ========== 小屏适配 ========== */
@media (max-width: 360px) {
  .ac-header { padding: 10px 14px; }
  .ac-body { padding: 12px 14px; gap: 10px; }
  .ac-phone { font-size: 14px; }
  .ac-balance { font-size: 20px; }
  .summary-value { font-size: 24px; }
  .listItem .title p { margin-left: 20%; }
  .listItem .title span { font-size: 12px; }
}
</style>
